/**
 * <cart-reservation-timer>: "Your cart is reserved for mm:ss min" at the top of the cart drawer
 * (port of the main shop's "Warenkorb v3" timer). The countdown starts at `data-minutes` once the cart
 * holds an item, survives page views and drawer re-renders (localStorage), stops at 00:00 and starts
 * over after the cart was emptied (`data-item-count="0"`, also reached by morphing an existing element).
 */
const STORAGE_KEY = 'vk_cart_reservation_end';

/** @type {number | null} Fallback when localStorage is unavailable (private mode): lives for this page view. */
let memoryEnd = null;

/** @returns {number | null} */
function readEnd() {
  try {
    return Number(localStorage.getItem(STORAGE_KEY)) || null;
  } catch {
    return memoryEnd;
  }
}

/** @param {number | null} end */
function writeEnd(end) {
  memoryEnd = end;
  try {
    if (end) localStorage.setItem(STORAGE_KEY, String(end));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // memoryEnd only
  }
}

/**
 * @param {number} seconds
 * @returns {string}
 */
function format(seconds) {
  return `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
}

class CartReservationTimer extends HTMLElement {
  static observedAttributes = ['data-item-count'];

  /** @type {ReturnType<typeof setInterval> | undefined} */
  #interval;

  /** @type {number | null} */
  #end = null;

  /** Re-ticks at once when a drawer morph resets the server-rendered "10:00". */
  #observer = new MutationObserver(() => this.#tick());

  connectedCallback() {
    this.#sync();
  }

  /** Horizon morphs the drawer in place, so the cart count arrives as an attribute change, not a new element. */
  attributeChangedCallback() {
    if (this.isConnected) this.#sync();
  }

  disconnectedCallback() {
    clearInterval(this.#interval);
    this.#observer.disconnect();
  }

  #sync() {
    clearInterval(this.#interval);
    this.#observer.disconnect();

    if (Number(this.dataset.itemCount || 0) === 0) {
      writeEnd(null);
      return;
    }

    this.#end = readEnd();
    if (!this.#end) {
      this.#end = Date.now() + Number(this.dataset.minutes || 10) * 60000;
      writeEnd(this.#end);
    }
    this.#tick();
    this.#interval = setInterval(() => this.#tick(), 1000);
    this.#observer.observe(this, { childList: true, characterData: true, subtree: true });
  }

  #tick() {
    const output = this.querySelector('[data-timer-time]');
    const seconds = Math.max(0, Math.ceil(((this.#end ?? 0) - Date.now()) / 1000));
    const text = format(seconds);
    if (output && output.textContent !== text) output.textContent = text;
    if (seconds === 0) clearInterval(this.#interval);
  }
}

if (!customElements.get('cart-reservation-timer')) {
  customElements.define('cart-reservation-timer', CartReservationTimer);
}
